import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Heart, BookOpen, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AboutPage = () => {
  const navigate = useNavigate();
  const [cmsContent, setCmsContent] = useState(null);

  useEffect(() => {
    fetchCMSContent();
  }, []);

  const fetchCMSContent = async () => {
    try {
      const response = await axios.get(`${API}/cms/about`);
      setCmsContent(response.data);
    } catch (error) {
      console.error('Failed to fetch about page content:', error);
    }
  };

  // Use CMS content with fallbacks
  const therapistName = cmsContent?.therapist_name || 'Kátia Aisha Belo Cardoso';
  const therapistTitle = cmsContent?.therapist_title || 'Psychoanalytic Psychotherapy';
  const therapistPhoto = cmsContent?.therapist_photo || 'https://customer-assets.emergentagent.com/job_kbc-therapy-platform/artifacts/eork56sw_Katia%20front%20facing.jpeg';
  const bioShort = cmsContent?.bio_short || 'I am a BACP registered psychoanalytic psychotherapist working with adults who are navigating trauma, oppressive or abusive relationships, and long-standing emotional distress.';

  const qualifications = cmsContent?.qualifications || [
    'BACP Registered Member (MBACP) #410538',
    'Psychoanalytic Psychotherapy',
    'Experience in NHS and community mental health settings',
    'Multilingual: English, Portuguese, Spanish, French',
    'Ongoing professional development and clinical supervision'
  ];

  const specialisations = cmsContent?.specializations || [
    'Childhood trauma and neglect',
    'Domestic and relational abuse',
    'Coercive control',
    'Racial trauma and discrimination',
    'Experiences of migration and living between cultures',
    'Difficulties with identity and belonging',
    'Borderline personality dynamics',
    'Intense emotions and fear of abandonment',
    'Difficulties with trust, intimacy, and attachment',
    'Repetitive patterns in relationships or work',
    'Feelings of emptiness, self-criticism, or internal conflict'
  ];

  const approachTitle = cmsContent?.approach_title || 'My Approach';
  const approachDescription = cmsContent?.approach_description || null;

  const values = [
    {
      icon: Heart,
      title: 'Depth & Reflection',
      description: 'I work long-term and at depth, believing that meaningful change comes through understanding ourselves more fully.'
    },
    {
      icon: BookOpen,
      title: 'Trauma-Informed',
      description: 'My practice is informed by trauma awareness and understanding how past experiences shape our present lives.'
    },
    {
      icon: Globe,
      title: 'Cultural Sensitivity',
      description: 'I understand how culture, language, race, and identity shape emotional experience and the expression of suffering.'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <p className="text-sm font-medium tracking-wide uppercase text-accent">
                About Me
              </p>
              <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight">
                {therapistName.split(' ').slice(0, 2).join(' ')}<br />
                {therapistName.split(' ').slice(2).join(' ')}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {bioShort}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                My practice is informed by psychoanalytic thinking, trauma awareness, and 
                an understanding of how culture, language, race, and identity shape 
                emotional experience.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-accent/20 rounded-3xl transform rotate-3" />
                <img
                  src={therapistPhoto}
                  alt={`${therapistName} - ${therapistTitle}`}
                  className="relative rounded-2xl shadow-xl object-cover w-full aspect-[4/5]"
                  data-testid="therapist-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl font-light mb-6">My Approach</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Psychoanalytic psychotherapy is a reflective, relational approach. We explore 
                  not only your immediate difficulties, but also how earlier experiences continue 
                  to shape your current thoughts, emotions, and relationships.
                </p>
                <p>
                  In our sessions, I pay attention to what may be repeating emotionally, to feelings 
                  that may feel overwhelming or difficult to name, and to responses that may be rooted 
                  in past harm, neglect, or abuse. I am interested in the parts of the self that may 
                  have been fragmented, silenced, or hidden.
                </p>
                <p>
                  I believe therapy works best when it offers consistency, containment, and time for 
                  joint thinking. Sessions typically take place once weekly at the same time, though 
                  some situations benefit from more frequent sessions.
                </p>
                <p>
                  I am not here to give you advice or solutions. Instead, I offer a space where you 
                  can begin to connect more fully with your own emotional truth, your own history, 
                  and your own inner authority.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl font-light mb-6">Qualifications</h2>
              <ul className="space-y-3">
                {qualifications.map((qual, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3 text-muted-foreground"
                    data-testid={`qualification-${index}`}
                  >
                    <Award className="h-5 w-5 text-primary flex-shrink-0" />
                    {qual}
                  </motion.li>
                ))}
              </ul>

              <div className="mt-8 p-6 bg-card rounded-2xl">
                <h3 className="font-heading text-xl mb-3">Languages</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  I offer therapy in <strong>English, Portuguese, Spanish, and French</strong>. 
                  Speaking in a language other than English can allow something different to emerge. 
                  You are welcome to choose whichever language feels safest, most private, or most 
                  emotionally genuine, and to switch between languages during sessions if that feels right.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl font-light mb-4">How I Work</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Core principles that guide my therapeutic practice
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)] text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="h-8 w-8 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-2xl font-normal mb-3">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialisations Section */}
      <section className="py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl font-light mb-6">Areas of Specialisation</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl">
              I have experience supporting individuals who have experienced a range of 
              emotional difficulties. You do not need a formal diagnosis to begin therapy.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {specialisations.map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.03 }}
                  className="flex items-center gap-3 p-4 bg-card rounded-xl"
                  data-testid={`specialisation-${index}`}
                >
                  <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who I Work With Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl"
          >
            <h2 className="font-heading text-4xl font-light mb-6">Who I Work With</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Psychoanalytic psychotherapy can be valuable for anyone seeking to understand 
                themselves more deeply, but it may be especially relevant if you:
              </p>
              <ul className="space-y-3 ml-4">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Feel shaped by early experiences that continue to affect your relationships, 
                  work, or sense of self</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Have experienced trauma, abuse, or neglect, and are seeking space to 
                  process these experiences</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Struggle with intense emotions, fear of abandonment, or unstable relationships</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Feel caught between identities, expectations, or languages</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Want to explore recurring patterns or unspoken issues that surface 
                  repeatedly in your life</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl font-light text-primary-foreground mb-4">
              Getting Here Took Courage
            </h2>
            <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              If something on this page resonates, I invite you to get in touch. 
              The initial consultation is a chance to meet, ask questions, and see 
              whether we are a good fit to work together.
            </p>
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full px-8 py-6"
              onClick={() => navigate('/book')}
              data-testid="about-cta-book-btn"
            >
              Book Initial Consultation · £50
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
